import type { OnboardingModalProps } from "../../AppModalTypes";
import type { SetupOption } from "../../onboarding/OnboardingSetupShopContent";
import type {
  AnnualRevenue as ShopAnnualRevenue,
  ProductDescription as ShopProductDescription,
} from "../../onboarding/OnboardingShopInfoContent";

import React, { useState } from "react";

import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import useInstallShopify from "@editor/hooks/useInstallShopify";
import { useModal } from "@editor/hooks/useModal";
import { onboardingRouter, onboardingUtils } from "@editor/trpc/onboarding";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { userUtils } from "@editor/trpc/user";
import { workspaceUtils } from "@editor/trpc/workspace";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isNullish } from "replo-utils/lib/misc";

import OnboardingSetupShopContent from "../../onboarding/OnboardingSetupShopContent";
import ShopInfo from "./ShopInfo";

export function OnboardingModal({
  workspaceId: propWorkspaceId,
}: OnboardingModalProps) {
  const { closeModal } = useModal();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const navigate = useNavigate();

  const [showSetupShopModal, setShowSetupShopModal] = useState<boolean>(false);
  const [selectedShopSetupOption, setSelectedShopSetupOption] =
    useState<SetupOption | null>("new");
  const [selectedProduct, setSelectedProduct] = useState<SetupOption | null>(
    "ai",
  );

  const { user } = useCurrentUser();
  const workspaceId = propWorkspaceId ?? currentWorkspaceId;

  const [shopName, setShopName] = useState<string>("");
  const [subdomain, setSubdomain] = useState<string>("");
  const [productDescription, setProductDescription] =
    useState<ShopProductDescription | null>(null);
  const [annualRevenue, setAnnualRevenue] = useState<ShopAnnualRevenue | null>(
    null,
  );

  const isShopifyPaymentProcessor =
    workspace?.preferredPaymentProcessor === "shopify";

  const { mutateAsync: createProjectAsync, isPending: isCreatingProject } =
    projectRouter.create.useMutation();

  const {
    mutateAsync: updateAnnualRevenueAsync,
    isPending: isUpdatingAnnualRevenue,
  } = onboardingRouter.updateAnnualRevenue.useMutation();

  const installShopify = useInstallShopify();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal({ type: "createShopFlowModal" });
    }
  };

  const mapRevenueToNumber = (revenue: string | null): number => {
    switch (revenue) {
      case "0-500k":
        return 0;
      case "500k-2m":
        return 500000;
      case "2m-5m":
        return 2000000;
      case "5m-10m":
        return 5000000;
      case "10m-50m":
        return 10000000;
      case "50m+":
        return 50000000;
      default:
        return 0;
    }
  };

  type ShopDetails = {
    shopName: string;
    subdomain: string;
    productDescription: ShopProductDescription | null;
    annualRevenue: ShopAnnualRevenue | null;
  };

  const handleShopInfoModalContinue = (
    projectId?: string,
    shopDetails?: ShopDetails,
  ) => {
    if (shopDetails) {
      setShopName(shopDetails.shopName);
      setSubdomain(shopDetails.subdomain);
      setProductDescription(shopDetails.productDescription);
      setAnnualRevenue(shopDetails.annualRevenue);
    }
    if (projectId) {
      closeModal({ type: "createShopFlowModal" });
      if (isShopifyPaymentProcessor) {
        navigate(`/shops/${projectId}`);
      } else {
        navigate(`/shops/${projectId}/home`);
      }
    } else {
      setShowSetupShopModal(true);
    }
  };

  const handleSetupShopContinue = async (_e: React.MouseEvent) => {
    const isCompleteFormValid =
      !isNullish(annualRevenue) &&
      productDescription !== null &&
      shopName !== "" &&
      subdomain !== "" &&
      user &&
      workspaceId &&
      selectedShopSetupOption &&
      selectedProduct;

    if (!isCompleteFormValid) {
      return;
    }

    await updateAnnualRevenueAsync({
      annualRevenue: mapRevenueToNumber(annualRevenue),
    });

    const hostingType = selectedProduct === "ai" ? "replo" : "shopify";

    const result = await createProjectAsync({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: hostingType,
      shopDomainPrefix: subdomain.trim(),
      productDescription: productDescription,
    });

    await workspaceUtils.getUserWorkspacesList.invalidate();
    await projectUtils.listWithStats.invalidate();
    await userUtils.get.invalidate();
    await projectUtils.listGroupedByWorkspaceForUser.invalidate();
    await onboardingUtils.checkSubdomainAvailability.invalidate();

    if (selectedShopSetupOption === "import") {
      void installShopify({
        workspaceId: workspaceId ?? undefined,
        cookie: {
          type: "onboarding",
          workspaceId: workspaceId ?? undefined,
          projectId: result.project?.id ?? undefined,
        },
        projectHostingType: hostingType,
      });
    } else {
      closeModal({ type: "createShopFlowModal" });
      navigate(`/shops/${result.project?.id}/home`);
    }
  };

  return (
    <>
      {!showSetupShopModal ? (
        <ShopInfo
          selectedWorkspaceId={workspaceId ?? undefined}
          onContinue={handleShopInfoModalContinue}
          onClose={() => {
            closeModal({ type: "createShopFlowModal" });
          }}
        />
      ) : (
        <Modal
          isOpen
          onOpenChange={handleOpenChange}
          size="custom"
          title="How do you want to set up your shop?"
          description="Let's get you up and running!"
          layoutClassName="h-[700px] w-[1000px]"
          UNSAFE_className="[&_.header-h2]:text-center [&_.header-h2]:text-3xl [&_.header-h2]:py-4 [&_.text-muted]:text-center [&_.text-muted]:pb-4 [&_[data-card-content]_.text-muted]:!text-left [&_[data-card-content]_.text-muted]:!pb-0"
          footer={
            <div className="w-full">
              <div className="flex justify-end items-center w-full pt-4 border-t-[0.5px] border-border px-6">
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="base"
                    onClick={() => setShowSetupShopModal(false)}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    size="base"
                    onClick={(e) => void handleSetupShopContinue(e)}
                    disabled={
                      isCreatingProject ||
                      isUpdatingAnnualRevenue ||
                      !selectedShopSetupOption ||
                      !selectedProduct
                    }
                    isLoading={isCreatingProject || isUpdatingAnnualRevenue}
                    endEnhancer={<ArrowRight size={16} />}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          <div className="flex items-start justify-center min-h-0 flex-1 overflow-y-auto py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pt-25">
            <div className="w-full max-w-4xl">
              <OnboardingSetupShopContent
                selectedHostingType={selectedShopSetupOption}
                setSelectedHostingType={setSelectedShopSetupOption}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                isShopContext={true}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
